<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type XlsFile, xlsFilesApi } from '@/api/entities/admin/xlsFiles'

definePage({
  meta: { title: 'XLS Files', action: 'read', subject: 'review' },
})

const { snackbar, handleError } = useErrorHandler()
const loading = ref(false)
const items = ref<XlsFile[]>([])
const currentPage = ref(1)
const lastPage = ref(1)

const deleteDialog = ref(false)
const deleteTarget = ref<XlsFile | null>(null)
const deleteLoading = ref(false)

const headers = [
  { title: '파일명', key: 'file_name', sortable: false },
  { title: '파일 타입', key: 'file_type', sortable: false },
  { title: '생성일', key: 'created_at', sortable: false },
  { title: '다운로드', key: 'download', sortable: false },
  { title: '작업', key: 'actions', sortable: false },
]

const loadItems = async () => {
  loading.value = true
  try {
    const { success, data } = await xlsFilesApi.list({ page: currentPage.value })
    if (success && data) {
      items.value = data.data
      lastPage.value = data.last_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

const onPageChange = (page: number) => {
  currentPage.value = page
  loadItems()
}

const openDeleteDialog = (item: XlsFile) => {
  deleteTarget.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  if (!deleteTarget.value) return
  deleteLoading.value = true
  try {
    const { success, error } = await xlsFilesApi.delete(deleteTarget.value.id)
    if (success) {
      snackbar.success('파일이 삭제되었습니다.')
      deleteDialog.value = false
      deleteTarget.value = null
      await loadItems()
    }
    else {
      snackbar.error(error?.message || '삭제 실패')
    }
  }
  catch (error) {
    handleError(error)
  }
  finally {
    deleteLoading.value = false
  }
}

const formatDate = (d: string | null) => d ? new Date(d).toLocaleString('ko-KR') : '-'

onMounted(loadItems)
</script>

<template>
  <VCard>
    <VCardTitle class="pa-4">
      XLS Files
    </VCardTitle>
    <VCardText>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
      >
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #item.download="{ item }">
          <VBtn
            v-if="item.presigned_url"
            size="small"
            color="primary"
            variant="outlined"
            :href="item.presigned_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            다운로드
          </VBtn>
          <span
            v-else
            class="text-disabled"
          >-</span>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            size="small"
            color="error"
            @click="openDeleteDialog(item)"
          >
            삭제
          </VBtn>
        </template>
        <template #no-data>
          <div class="text-center py-8">
            데이터가 없습니다.
          </div>
        </template>
      </VDataTable>
      <div
        v-if="lastPage > 1"
        class="d-flex justify-center mt-4"
      >
        <VPagination
          :model-value="currentPage"
          :length="lastPage"
          @update:model-value="onPageChange"
        />
      </div>
    </VCardText>
  </VCard>

  <VDialog
    v-model="deleteDialog"
    max-width="400"
  >
    <VCard>
      <VCardTitle class="pa-4">
        파일 삭제 확인
      </VCardTitle>
      <VCardText>
        <p>다음 파일을 삭제하시겠습니까?</p>
        <p
          v-if="deleteTarget"
          class="font-weight-bold mt-2"
        >
          {{ deleteTarget.file_name }}
        </p>
        <p class="text-warning mt-2">
          이 작업은 되돌릴 수 없습니다.
        </p>
      </VCardText>
      <VCardActions class="pa-4 justify-end">
        <VBtn
          variant="outlined"
          @click="deleteDialog = false"
        >
          취소
        </VBtn>
        <VBtn
          color="error"
          :loading="deleteLoading"
          @click="confirmDelete"
        >
          삭제
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>
